import React, {useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';
import Pagination from '../../../Common/Pagination/PaginationMotif';
import MotifTable from '@ey/motif-react-wrapper/Table';
import MotifSearch from '@ey/motif-react-wrapper/Search';
import MotifChip from '@ey/motif-react-wrapper/Chip';
import MotifTooltip from '@ey/motif-react-wrapper/Tooltip';
import MotifFormField from '@ey/motif-react-wrapper/FormField';
import EllipsesControl from '../../../Common/EllipsesControl/EllipsesControl';
import moment from 'moment';
import Loader from '../../../Common/Loaders/loadingindicator';
import {
	pagingOptions,
	labels,
	dateFormatsListItems,
	dateFormats,
	successMessageDisplayDuration,
	currentResource
} from '../../../../util/uiconstants';
import env from '../../../../util/env';
import {getEngagementTemplates} from '../../../../actions/admin/templatemanagementaction';
import AccessDeniedMessage from '../../../Common/AccessDeniedMessage/AccessDeniedMessage';
import TemplateDetails from '../templatedetails/templatedetails';
import ModalControl from '../../../Common/Modal/Modal';
import LocalToast from '../../../Common/Toast/Toast';
import {clearErrors} from '../../../../actions/erroractions';
import {setIsModalOpen} from '../../../../actions/modalstateactions';
import {MotifIcon, MotifIconButton} from '@ey-xd/motif-react';
import {IconoirTrash, IconoirEditPencil} from '@ey-xd/motif-icon';
import TemplateDelete from '../templatedelete/templatedelete';

function TemplateManagement() {
	// hooks
	const engagementTemplate = useSelector((store) => store.engagementTemplates);
	const userPermissions = useSelector((store) => store.userPermissions);
	const dispatch = useDispatch();
	const history = useHistory();

	//states
	const [searchInputValue, setSearchInputValue] = useState('');
	const [page, setPage] = useState(1);
	const [optionPerPage, setOptionPerPage] = useState(pagingOptions.options[0]);
	const [totalPages, setTotalPages] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isTemplateEdited, setIsTemplateEdited] = useState(false);
	const [isTemplateDeleted, setIsTemplateDeleted] = useState(false);
	const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);
	const [editTemplateData, seteditTemplateData] = useState(null);
	const [showdeleteTemplateData, setShowDeleteTemplateData] = useState(false);
	const [editTemplateSuccessMessage, setEditTemplateSuccessMessage] =
		useState('');
	const [deleteTemplateData, setDeleteTemplateData] = useState(null);

	const duration = successMessageDisplayDuration;

	useEffect(() => {
		loadEngagementTemplates(searchInputValue);
	}, [page, optionPerPage]);

	useEffect(() => {
		if (isTemplateEdited) {
			loadEngagementTemplates(searchInputValue);
			setIsTemplateEdited(false);
		}
	}, [isTemplateEdited]);

	useEffect(() => {
		if (isTemplateDeleted) {
			loadEngagementTemplates(searchInputValue);
			setIsTemplateDeleted(false);
		}
	}, [isTemplateDeleted]);

	useEffect(() => {
		if (engagementTemplate) {
			setTotalPages(engagementTemplate?.pages);
		}
	}, [engagementTemplate]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setEditTemplateSuccessMessage('');
		}, duration);
		return () => clearTimeout(timer);
	});

	useEffect(() => {
		if (editTemplateData == true) {
			dispatch(clearErrors()).then(function () {
				dispatch(setIsModalOpen(true));
			});
		} else {
			dispatch(clearErrors());
			dispatch(setIsModalOpen(false));
		}
	}, [editTemplateData]);

	// Use common error clearing framework to clear axios errors
	const clearError = (isModelOpen) => {
		dispatch(clearErrors()).then(() => {
			dispatch(setIsModalOpen(isModelOpen));
		});
	};

	useEffect(() => {
		clearError(true);
	}, []);

	const closeEditTemplateModal = () => {
		setShowEditTemplateModal(false);
		setShowDeleteTemplateData(false);
		seteditTemplateData(null);
		setDeleteTemplateData(null);
		let path = history.location.pathname + history.location.search;
		if (history.location.hash && history.location.hash.length > 0) {
			history.push(path);
		}
	};

	const loadEngagementTemplates = (searchText) => {
		setIsLoading(true);
		let adminUserUrl =
			env.getURL('serviceUrlV2') +
			`admin/EngagementTemplates?page=${page}&pageSize=${optionPerPage}`;

		if (searchText) {
			const encodedSearchText = encodeURIComponent(searchText);
			adminUserUrl += `&searchString=${encodedSearchText}`;
		}

		dispatch(getEngagementTemplates(adminUserUrl))
			.then(() => {
				setIsLoading(false);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const onSearchChange = () => {
		if (searchInputValue != undefined) {
			setPage(1);
			loadEngagementTemplates(searchInputValue);
		}
	};

	const onClearClick = () => {
		setSearchInputValue('');
		loadEngagementTemplates('');
	};

	const paginationHandler = (value) => {
		if (page !== value && page > 0) {
			setPage(value);
		}
	};
	const handlerOptionPage = (value) => {
		if (value !== optionPerPage) {
			setOptionPerPage(value);
			setPage(1);
		}
	};

	const columnDefs = [
		{
			flex: 2,
			headerName: labels.templateName,
			field: 'templateName',
			cellRenderer: (params) => cellTemplateNameRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 2,
			headerName: labels.templateAreaRegion,
			field: 'area',
			cellRenderer: (params) => cellAreaRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 1,
			headerName: labels.templateSize,
			field: 'filesize',
			cellRenderer: (params) => cellTemplateSizeRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 1,
			headerName: labels.templateStatus,
			field: 'isPublished',
			cellRenderer: (params) => cellTemplateStatusRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			felx: 3,
			headerName: labels.templateCreatedBy,
			field: 'createdUserName',
			cellRenderer: (params) => cellCreatedUserRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			felx: 1,
			headerName: labels.templateCreatedDate,
			field: 'createDate',
			cellRenderer: (params) => createDateCellRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 1,
			headerName: labels.templateCountOfEngagement,
			field: 'successfulTemplateImportCount',
			cellRenderer: (params) => cellCountOfEngagementRender(params),
			lockPosition: true,
			suppressSizeToFit: true
		},
		{
			flex: 1,
			headerName: '',
			field: 'actions',
			cellRenderer: (params) => actionsRenderer(params),
			lockPosition: true,
			suppressSizeToFit: true
		}
	];

	const ellipsesControlRender = (text) => {
		return (
			<section className="roleType motif-body2-default-light">
				<EllipsesControl
					content={text}
					tooltip={text}
					displayId={''}
					noOfLines={1}
					ellipsisClass="columnName"
				/>
			</section>
		);
	};

	const onTemplateDetailslick = (data) => {
		setShowEditTemplateModal(true);
		seteditTemplateData(data);
	};

	const cellTemplateNameRender = (e) => {
		return (
			<section
				className="roleType templateName-cell motif-body2-default-light"
				onClick={() => onTemplateDetailslick(e.data)}
			>
				{ellipsesControlRender(e.data.templateName)}
			</section>
		);
	};

	const cellAreaRender = (e) => {
		let region = '';
		if (e.data?.regions != undefined && e.data?.regions?.length > 0) {
			region =
				e.data?.regions?.length > 1
					? (region = e.data?.regions
							?.map((item) => {
								return item?.regionName;
							})
							.join(', '))
					: e.data?.regions[0].regionName;
		}

		return <>{ellipsesControlRender(region)}</>;
	};

	const cellTemplateStatusRender = (e) => {
		return (
			<>
				{e.data.isPublished ? (
					<MotifChip type={'outlined'}>{labels.templatePublished}</MotifChip>
				) : null}
			</>
		);
	};

	const cellTemplateSizeRender = (e) => {
		return <section className="templateSize">{e.data.filesize}</section>;
	};

	const cellCreatedUserRender = (e) => {
		return <>{ellipsesControlRender(e.data.createdUserName)}</>;
	};

	const createDateCellRender = (e) => {
		let createDate = e.data.createDate
			? moment
					.utc(e.data.createDate)
					.format(dateFormatsListItems[dateFormats].label)
			: '';
		return <>{ellipsesControlRender(createDate)}</>;
	};

	const cellCountOfEngagementRender = (e) => {
		return (
			<section className="countOfEng">
				{e.data.successfulTemplateImportCount}
			</section>
		);
	};

	const onTemplateDelete = (data) => {
		setShowDeleteTemplateData(true);
		setDeleteTemplateData(data);
	};
	const actionsRenderer = (e) => {
		return (
			<section className="iconbutton">
				<section className="editicon-button">
					<MotifTooltip
						id={'edit'}
						flip={false}
						hideCloseButton
						placement="left"
						contentClassName="editicon"
						hide={userPermissions?.permissions?.canUpdateTemplates}
						trigger={
							<section>
								<MotifIconButton
									type="button"
									aria-label="edit-icon-btn"
									disabled={!userPermissions?.permissions?.canUpdateTemplates}
									onClick={() => {
										onTemplateDetailslick(e.data);
									}}
									className="edit-user-edit-btn"
								>
									<MotifIcon
										name="edit"
										className="edit-icon"
										iconFunction={IconoirEditPencil}
										title={labels.editHoverText}
										size={24}
									/>
								</MotifIconButton>
							</section>
						}
					>
						{labels.insufficientPermission}
					</MotifTooltip>
				</section>
				<section className="deleteicon-button">
					<MotifTooltip
						id={'deletebButtonTooltipId'}
						hideCloseButton
						placement="left"
						flip={false}
						contentClassName="deleteicon"
						hide={userPermissions?.permissions?.canDeleteTemplates}
						trigger={
							<section>
								<MotifIconButton
									type="button"
									aria-label="delete-icon"
									disabled={!userPermissions?.permissions?.canDeleteTemplates}
									onClick={() => {
										onTemplateDelete(e.data);
									}}
									className="delete-user-delete-btn"
								>
									<MotifIcon
										name="delete"
										className="delete-icon"
										iconFunction={IconoirTrash}
										title={labels.messagingModalDeleteButton}
										size={24}
									/>
								</MotifIconButton>
							</section>
						}
					>
						{labels.insufficientPermission}
					</MotifTooltip>
				</section>
			</section>
		);
	};

	function adminPermissions() {
		let permissionGroupPermission = userPermissions?.permissions;
		return (
			permissionGroupPermission &&
			(permissionGroupPermission.canReadTemplates ||
				permissionGroupPermission.canUpdateTemplates ||
				permissionGroupPermission.canDeleteTemplates)
		);
	}

	const getRowStyle = (params) => {
		if (params.node.rowIndex % 2 === 0) {
			return {background: 'var(--neutrals-00white)'};
		} else {
			return {background: 'var(--ui-background-secondary)'};
		}
	};

	return (
		<Mainwrapper>
			{adminPermissions() ? (
				<section>
					<section className="templatemanagement mainResponsiveWrapper">
						<section
							className={
								'toastMessage ' +
								(editTemplateSuccessMessage.length > 0 ? 'block' : 'none')
							}
						>
							<LocalToast
								variant="success"
								duration={duration}
								onClose={() => setEditTemplateSuccessMessage('')}
							>
								{editTemplateSuccessMessage}
							</LocalToast>
						</section>
						<section className="add-on">
							<section className="search">
								<MotifFormField>
									<MotifSearch
										placeholder={labels.searchUserText}
										value={searchInputValue}
										onChange={(e) => setSearchInputValue(e.target.value)}
										onSearchButtonClick={onSearchChange}
										onClear={onClearClick}
										onEnter={onSearchChange}
										clearHoverText={labels.clear}
									/>
								</MotifFormField>
							</section>
						</section>
					</section>

					{isLoading ? (
						<Loader show view="blockView" styleName="loadingstyle" />
					) : engagementTemplate?.totalEntityCount > 0 ? (
						<section className="templatemanagementTable mainResponsiveWrapper">
							<MotifTable
								defaultColDef={{
									flex: 1,
									sortable: false,
									filter: false,
									resizable: false,
									minWidth: 180,
									lockPosition: true
								}}
								enableCellTextSelection
								getRowStyle={getRowStyle}
								rowHeight={48}
								headerHeight={48}
								suppressCellSelection
								columnDefs={columnDefs}
								onGridReady={(params) => {
									params.api.sizeColumnsToFit();
								}}
								rowSelection="multiple"
								rowData={engagementTemplate?.data}
								rowBuffer={200}
								frameworkComponents={{
									cellTemplateNameRender: cellTemplateNameRender,
									cellCreatedUserRender: cellCreatedUserRender,
									cellAreaRender: cellAreaRender,
									cellCountOfEngagementRender: cellCountOfEngagementRender,
									cellTemplateSizeRender: cellTemplateSizeRender,
									createDateCellRender: createDateCellRender,
									cellTemplateStatusRender: cellTemplateStatusRender,
									actionsRenderer: actionsRenderer
								}}
							/>
						</section>
					) : (
						<section className="norecord">{labels.noResultsFound}</section>
					)}

					{!isLoading && (
						<section className="pagerholder">
							<Pagination
								styleName="messaging-paging mainResponsiveWrapper"
								options={pagingOptions.options}
								pageSize={optionPerPage}
								showText={currentResource.common.pagination.show}
								onPageSizeChange={(pageSize) => {
									handlerOptionPage(pageSize);
								}}
								totalPages={totalPages}
								currentPage={page}
								onPageChange={(pageNumber) => {
									paginationHandler(pageNumber);
								}}
							/>
						</section>
					)}

					<ModalControl
						className="edit-template"
						title={labels.templateEdit}
						onOkClick={() => {}}
						onHide={() => closeEditTemplateModal(false)}
						isOkButtonDisabled={false}
						show={showEditTemplateModal}
						modalsize="small"
						showButtons={false}
						closeTitle={labels.close}
						modalContainerClass="edit-template"
						role="dialog"
						focusTrapActive={false}
					>
						<TemplateDetails
							setIsTemplateEdited={setIsTemplateEdited}
							closeEditTemplateModal={closeEditTemplateModal}
							editTemplateData={editTemplateData}
							setEditTemplateSuccessMessage={setEditTemplateSuccessMessage}
						/>
					</ModalControl>

					<section className="deletetemplate">
						<ModalControl
							className="delete-template"
							title={labels.templateDelete}
							onOkClick={() => {}}
							onHide={() => closeEditTemplateModal(false)}
							isOkButtonDisabled={true}
							show={showdeleteTemplateData}
							modalsize="x-small-secondary"
							showButtons={false}
							closeTitle={labels.close}
							modalContainerClass="delete-template"
							role="dialog"
							focusTrapActive={false}
						>
							<TemplateDelete
								deleteTemplateData={deleteTemplateData}
								closeEditTemplateModal={closeEditTemplateModal}
								setIsTemplateDeleted={setIsTemplateDeleted}
							/>
						</ModalControl>
					</section>
				</section>
			) : (
				<AccessDeniedMessage permissionMesage={labels.insufficientPermission} />
			)}
		</Mainwrapper>
	);
}

const Mainwrapper = styled.section`
	overflow: hidden;
	padding-top: var(--spacing-xx-large);
	.iconbutton {
		display: flex;
		gap: var(--spacing-large);
	}

	.editicon-button {
		display: flex;
		button.edit-user-edit-btn {
			display: flex;
			&[disabled] {
				opacity: 0.5;
				pointer-events: none;
			}
		}
	}

	.deleteicon-button {
		display: flex;
		button.delete-user-delete-btn {
			display: flex;
			&[disabled] {
				opacity: 0.5;
				pointer-events: none;
			}
		}
	}

	.loadingstyle {
		position: absolute;
		top: 50%;
		left: 50%;
	}

	//heading
	.templatemanagement {
		.toastMessage {
			&.block {
				display: block;
			}

			&.none {
				display: none;
			}
		}
	}

	//addon

	.add-on {
		display: flex;
		margin-right: auto;
		padding-bottom: var(--spacing-x-large);

		.search {
			width: var(--px-400);
			margin-right: auto;
		}
	}

	//pagination

	.pagerholder {
		width: calc(100vw - var(--px-80));
		margin: 0 auto;
		max-width: 2048px;
		display: flex;
		padding: var(--spacing-x-large) 0;
	}

	.norecord {
		height: calc(100vh - var(--px-250));
		display: inline-block;
		width: 100%;
		display: flex;
		justify-content: center;
		flex: 0 auto;
		align-items: center;
		font-size: var(--px-16);
		color: var(--neutrals-700);
	}

	.templatemanagementTable {
		overflow-y: auto;
		.motif-table {
			height: calc(100vh - var(--px-331)) !important;

			.ag-header-cell-text {
				font-size: var(--px-14) !important;
				font-weight: 400 !important;
				line-height: var(--px-20) !important;
				color: var(--neutrals-900);
			}

			.ag-header-cell .ag-header-select-all {
				visibility: hidden;
			}
			.ag-body {
				background: var(--neutrals-00white);
			}

			.ag-body-viewport {
				height: 100%;

				&:after {
					content: none;
				}
			}

			.ag-row,
			.ag-row-hover {
				border: none;
			}
			.ag-root.ag-layout-normal {
				overflow-y: auto;
				width: 100%;
			}

			.ag-root.ag-layout-auto-height {
				display: block;
			}

			.ag-center-cols-viewport {
				overflow-x: hidden;
			}

			.ag-cell-wrapper {
				display: grid;
				width: 100%;
			}
			.ag-header-cell:last-child,
			.ag-cell:last-child {
				display: flex;
				text-align: right;
				justify-content: center;
			}

			.roleType.disabled,
			.roleType.disabled * {
				color: var(--neutrals-300) !important;
			}
			.noptr {
				pointer-events: none;
			}
			.disabled {
				color: var(--neutrals-300) !important;
			}
			.templateName-cell {
				color: var(--blue-600) !important;
				cursor: pointer;
				.ellipses {
					color: var(--blue-600);
				}
			}

			.templateSize,
			.countOfEng {
				color: var(--ui-text-main);
			}

			.roleType {
				display: inline-block;
				width: 100%;
				align-items: center;
				color: var(--ui-text-main);

				.ellipses {
					margin: 0 !important;
					padding: 0 !important;
					display: inline-flex;
				}
			}
		}

		.errorRow {
			color: var(--red-600) !important;
		}
	}
`;

export default TemplateManagement;
